import React, { useEffect, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import _ from 'lodash';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
    Text,
    EditIcon,
    DeleteIcon,
    HorizontalRule,
    Tag,
    IconButton,
    TagSelect,
    ContainedButton,
    ConfirmDialog,
    NoInformation,
} from '../../index';

const Note = ({ data: noteData, index, handleDeleteNote, handleSaveNote, tags, emotions }) => {
    const [editing, setEditing] = useState(false);
    const [data, setData] = useState({});
    const [description, setDescription] = useState('');
    const [selectedEmotion, setSelectedEmotion] = useState(['']);
    const [providerTag, setProviderTags] = useState([]);
    const [showDialog, setShowDialog] = useState(false);

    const handleReset = () => {
        setData(noteData);

        if (!_.isEmpty(noteData.emotions)) {
            setSelectedEmotion([''].concat(noteData.emotions.map((e) => e.id)));
        } else {
            setSelectedEmotion(['']);
        }
        if (!_.isEmpty(noteData.provider_tags)) {
            setProviderTags(noteData.provider_tags.map((t) => t.id));
        }
        setDescription(noteData.description);
        setEditing(false);
    };

    useEffect(() => {
        handleReset();
        if (noteData.newNote) {
            setEditing(true);
        }
    }, [noteData]);

    const handleCancel = () => {
        if (!noteData.newNote) {
            handleReset();
        } else {
            handleDeleteNote(data.id);
        }
    };

    const handleEmotionChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedEmotion(value);
    };

    const handleNoteChange = (value) => {
        setDescription(value);
    };

    const handleSave = () => {
        const newData = { ...data, index };
        newData.description = description;
        newData.emotions = selectedEmotion.filter((e) => e !== '' && e !== undefined);
        newData.provider_tags = providerTag;
        delete newData.newNote;
        setEditing(false);
        setData(newData);
        handleSaveNote(newData);
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '10px',
                marginTop: '10px',
                minheight: '91px',
            }}
        >
            <ConfirmDialog
                open={showDialog}
                contentText="Are you sure you want to delete this note ?"
                cancelText="Cancel"
                onCancel={() => setShowDialog(false)}
                confirmText="Delete"
                onConfirm={() => {
                    handleDeleteNote(data.id);
                    setShowDialog(false);
                }}
            />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ display: 'flex', columnGap: '15px' }}>
                    <Text $color="gray2" $fontSize="12px">
                        {dayjs(data.createdAt).format('HH:mm')}
                    </Text>
                    <Text $color="gray2" $fontSize="12px">
                        {data.patient ? data.patient?.user?.fullname : 'Note'}
                    </Text>
                </div>
                <div style={{ display: 'flex', columnGap: '15px' }}>
                    <IconButton onClick={() => setEditing(true)}>
                        <EditIcon width="16px" height="16px" fill={editing ? 'pink' : 'gray1'} />
                    </IconButton>
                    <IconButton onClick={() => setShowDialog(true)}>
                        <DeleteIcon width="16px" height="16px" fill="gray1" />
                    </IconButton>
                </div>
            </div>

            {editing ? (
                <TextField
                    id="note-textfield"
                    multiline
                    variant="outlined"
                    fullWidth
                    autoFocus
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                    }}
                    InputProps={{
                        style: {
                            borderRadius: '16px',
                        },
                    }}
                    value={description}
                    onChange={(e) => handleNoteChange(e.target.value)}
                    inputProps={{ maxLength: 2000 }}
                />
            ) : (
                <Text $fontSize="14px" style={{ width: '85%' }}>
                    {description}
                </Text>
            )}

            {editing ? (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                        <TagSelect
                            value={selectedEmotion}
                            onChange={handleEmotionChange}
                            options={emotions}
                        />
                        <div style={{ display: 'flex', columnGap: '5px' }}>
                            {selectedEmotion.slice(1).map((e, index) => (
                                <Text
                                    key={e}
                                    $color="gray3"
                                    $fontSize="12px"
                                    style={{ textTransform: 'capitalize' }}
                                >
                                    {selectedEmotion.length - 1 !== index + 1
                                        ? `${
                                              emotions.find((emotion) => emotion.id === e)
                                                  .strEmotion
                                          },`
                                        : emotions.find((emotion) => emotion.id === e).strEmotion}
                                </Text>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', columnGap: '25px' }}>
                        <Text onClick={handleCancel}>Cancel</Text>
                        <ContainedButton
                            onClick={handleSave}
                            style={{
                                height: '24px',
                                paddingLeft: '20px',
                                paddingRight: '20px',
                            }}
                        >
                            Save
                        </ContainedButton>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', columnGap: '5px' }}>
                    {!_.isEmpty(selectedEmotion) &&
                        !_.isEmpty(emotions) &&
                        selectedEmotion.slice(1).map((e) => (
                            <Tag
                                $backgroundColor="purple"
                                key={`emotion${e}`}
                                style={{ textTransform: 'capitalize' }}
                            >
                                {emotions.find((emotion) => emotion.id === e)?.strEmotion}
                            </Tag>
                        ))}
                    {!_.isEmpty(providerTag) &&
                        !_.isEmpty(tags) &&
                        providerTag.map((tag) => (
                            <Tag key={tag}>{tags.find((t) => t.id === tag)?.name}</Tag>
                        ))}
                </div>
            )}
            <HorizontalRule />
        </div>
    );
};

Note.propTypes = {
    data: PropTypes.object,
    handleDeleteNote: PropTypes.func,
    tags: PropTypes.array,
    emotions: PropTypes.array,
    index: PropTypes.number,
    handleSaveNote: PropTypes.func,
};

const ProviderNote = ({ data, handleSaveNote, handleDeleteNote, tags, emotions }) => {
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                    paddingLeft: '24px',
                    paddingRight: '24px',
                }}
            >
                {data && data.length > 0 ? (
                    data.map((note, index) => (
                        <Note
                            key={`providerNote${index}${note.id}`}
                            data={note}
                            index={index}
                            handleSaveNote={handleSaveNote}
                            handleDeleteNote={handleDeleteNote}
                            tags={tags}
                            emotions={emotions}
                        />
                    ))
                ) : (
                    <NoInformation />
                )}
            </div>
        </div>
    );
};

ProviderNote.propTypes = {
    data: PropTypes.array.isRequired,
    handleSaveNote: PropTypes.func.isRequired,
    handleDeleteNote: PropTypes.func.isRequired,
    tags: PropTypes.array.isRequired,
    emotions: PropTypes.array.isRequired,
};

export default ProviderNote;
